import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { STAFF } from "../../constants/testIds";
import { FormField, isFieldValid } from "../common/FormField";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

const EMPTY = { name: "", email: "", password: "", kairoo_role_ref: "" };

export function CreateKairooUserDialog({ roles, open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();

  const createMutation = useMutation({
    mutationFn: () => api.post("/control/kairoo-users", form),
    onSuccess: () => pulse(() => {
      toast.success(`${form.name} added to the Control Plane`);
      queryClient.invalidateQueries({ queryKey: ["kairoo-users"] });
      setForm(EMPTY);
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const formValid = isFieldValid("text", true, form.name) && isFieldValid("email", true, form.email)
    && form.password.length >= 12 && !!form.kairoo_role_ref;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal" data-testid={STAFF.createUserDialog}>
        <DialogHeader><DialogTitle>Add a Kairoo staff member</DialogTitle></DialogHeader>
        <FormField label="Name" required testId={STAFF.userNameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <FormField label="Email" type="email" required testId={STAFF.userEmailInput} value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <div className="space-y-1.5">
          <Label htmlFor="ku-password">Temporary password <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
          <Input id="ku-password" type="password" data-testid={STAFF.userPasswordInput} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Min 12 characters. MFA is mandatory the moment they first sign in.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Role <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
          <Select value={form.kairoo_role_ref} onValueChange={(v) => setForm({ ...form, kairoo_role_ref: v })}>
            <SelectTrigger data-testid={STAFF.userRoleSelect}><SelectValue placeholder="Choose a role…" /></SelectTrigger>
            <SelectContent>
              {(roles || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <SubmitButton
            testId={STAFF.createUserSubmit} disabled={!formValid} pending={createMutation.isPending} success={success}
            onClick={() => createMutation.mutate()} label="Add staff member" successLabel="Added"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
